'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import {
  useDashboardEvaluationsQuery,
  useDashboardWorkspaceQuery,
} from './dashboardQuery'
import {
  DashboardEvaluationsQuery,
  GetClientSetupQuery,
  MembersListQuery,
  UserQuery,
} from '~/gql/graphql'
import { QueryStatus } from '@tanstack/react-query'
import { useUserMetadata } from '~/hooks/useUserMetadata'
import { useUserQuery } from './userQuery'
import { useWorkspaceMembersQuery } from './workspace/modals/memberQuery'
import { useAppliedUserId } from '~/hooks/useAppliedUserId'
import { recordDashboardHeartbeat } from './heartbeat'
import {
  ClienSetupQueryResult,
  UseClientSetupQueryResult,
  useClientSetupQuery,
} from './clientSetupQuery'

const shouldDebug = () => {
  // Check NEXT_PUBLIC_ENV (used in docker) or NEXT_PUBLIC_VERCEL_ENV (used in Vercel)
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV
  const nodeEnv = process.env.NODE_ENV

  return (
    env === 'dev' ||
    env === 'staging' ||
    env === 'development' ||
    env === 'preview' ||
    nodeEnv === 'development'
  )
}

type IDashboardWorkspaceContext = {
  owner: {
    memberId: string
  }
  workspace: {
    id: string
    parentId: string
    commonName: string
    billingCode: string
    showBillingCode: boolean
    orgName: string
    creatorId: string
  }
  memberId: string
  member?: MembersListQuery['getWorkspaceMembers'][0]
  workspaceMembers: {
    workspaceMembersQueryStatus: 'loading' | 'error' | 'success'
    refetchWorkspaceMembers: () => void
    members: MembersListQuery['getWorkspaceMembers']
  }
  /**
   * User profile object from our GraphQL user query.
   * Prefer using this over auth0's useUser hook
   * to display user profile information such
   * as name and avatar in the UI.
   */
  userProfile?: UserQuery['user']['profile']
  refetch?: () => void
  status?: QueryStatus
  findMemberById: (memberId: string) => unknown
  findMemberEmail: (memberId: string) => string | undefined
  clientSetup?: ClienSetupQueryResult
}

const defaultDashboardWorkspaceContextValue: IDashboardWorkspaceContext = {
  owner: {
    memberId: '',
  },
  workspace: {
    id: '',
    parentId: '',
    commonName: '',
    billingCode: '',
    showBillingCode: false,
    orgName: '',
    creatorId: '',
  },
  member: undefined,
  memberId: '',
  workspaceMembers: {
    workspaceMembersQueryStatus: 'loading',
    refetchWorkspaceMembers: () => {},
    members: [],
  },
  userProfile: undefined,
  status: undefined,
  findMemberById: () => undefined,
  findMemberEmail: () => undefined,
  clientSetup: undefined,
}

export const DashboardWorkspaceContext =
  createContext<IDashboardWorkspaceContext>(
    defaultDashboardWorkspaceContextValue
  )

export const useDashboardWorkspaceContext = () =>
  useContext(DashboardWorkspaceContext)

export const DashboardWorkspaceContextProvider = ({ children }) => {
  // Always use the authoritative member_id from backend (verified by signed token)
  // This handles user conversion/matching automatically
  // The backend reads member_id directly from the JWT token's custom claims
  const { memberId: appliedMemberId, isLoading: isLoadingMemberId } =
    useAppliedUserId()

  console.log(`[DashboardWorkspaceContext] appliedMemberId=${appliedMemberId} isLoadingMemberId=${isLoadingMemberId}`)

  // Use appliedMemberId as source of truth - do NOT fallback to userMetadata which may contain system user ID
  // The backend's appliedUserId query reads from the token, which is the authoritative source
  const memberIdToUse = useMemo(() => {
    const SYSTEM_USER_ID = '409b6857-a544-4aa2-a3f1-9747be7958d5'
    if (appliedMemberId === SYSTEM_USER_ID) {
      console.error(
        `[CRITICAL] DashboardWorkspaceContext: appliedMemberId is system user ID: ${appliedMemberId}. ` +
          `This should never happen. Check useAppliedUserId hook.`
      )
    }
    const result = appliedMemberId || ''
    console.log(`[DashboardWorkspaceContext] memberIdToUse=${result}`)
    return result
  }, [appliedMemberId])
  // Only enable queries when we have a valid memberId - memoize to prevent unnecessary query re-runs
  const hasMemberId = useMemo(() => {
    const result = !!memberIdToUse
    console.log(`[DashboardWorkspaceContext] hasMemberId=${result} — dashboard query ${result ? 'ENABLED' : 'DISABLED'}`)
    return result
  }, [memberIdToUse])
  const { data: userQueryData } = useUserQuery(memberIdToUse)
  const { status, data, refetch, loadSessionId, loadSessionIdRef } =
    useDashboardWorkspaceQuery(hasMemberId)
  const hasRecordedLoadEndRef = useRef(false)

  // Move debug logging to useEffect to prevent logging on every render
  useEffect(() => {
    if (shouldDebug()) {
      console.log(
        `[DEBUG] DashboardWorkspaceContext: status=${status}, hasData=${!!data}, timestamp=${new Date().toISOString()}`
      )
    }
  }, [status, data])

  const dashboardData = data?.dashboard

  // Memoize workspaceId to prevent unnecessary query re-runs
  const workspaceId = useMemo(
    () => dashboardData?.workspace?.id ?? '',
    [dashboardData?.workspace?.id]
  )

  const { data: clientSetup } = useClientSetupQuery(
    workspaceId,
    Boolean(workspaceId)
  )
  const clientSetupData = clientSetup?.getClientSetup as ClienSetupQueryResult

  // Only enable workspace members query when we have both memberId and workspaceId
  // Memoize to prevent unnecessary recalculations
  const shouldFetchWorkspaceMembers = useMemo(
    () => hasMemberId && !!workspaceId,
    [hasMemberId, workspaceId]
  )

  const {
    status: workspaceMembersQueryStatus,
    data: workspaceMembersData,
    refetch: refetchWorkspaceMembersQuery,
    isFetchedAfterMount,
  } = useWorkspaceMembersQuery({
    workspaceId,
    enabled: shouldFetchWorkspaceMembers,
  })

  // Debug: Log when query status changes
  useEffect(() => {
    if (shouldDebug()) {
      console.log(
        `[DEBUG] DashboardWorkspaceContext: workspaceMembersQuery status=${workspaceMembersQueryStatus}, workspaceId=${workspaceId}, enabled=${shouldFetchWorkspaceMembers}, membersCount=${
          workspaceMembersData?.getWorkspaceMembers?.length ?? 0
        }`
      )
    }
  }, [
    workspaceMembersQueryStatus,
    workspaceId,
    shouldFetchWorkspaceMembers,
    workspaceMembersData,
  ])

  const refetchWorkspaceMembers = useCallback(() => {
    if (isFetchedAfterMount) {
      refetchWorkspaceMembersQuery()
    }
  }, [isFetchedAfterMount, refetchWorkspaceMembersQuery])

  // Memoize workspaceMembers array to prevent unnecessary context value changes
  const workspaceMembers = useMemo(
    () => workspaceMembersData?.getWorkspaceMembers ?? [],
    [workspaceMembersData?.getWorkspaceMembers]
  )

  const member = useMemo(
    () =>
      workspaceMembers?.find(
        (member) => member?.user?.memberId === memberIdToUse
      ),
    [workspaceMembers, memberIdToUse]
  )

  const findMemberById = useCallback(
    (memberId: string) =>
      dashboardData?.workspace?.members?.find(
        (member) => member?.user?.memberId === memberId
      ),
    [dashboardData]
  )
  const findMemberEmail = useCallback(
    (memberId: string) => findMemberById(memberId)?.user?.profile?.email,
    [findMemberById]
  )

  // Heartbeat tracking - send heartbeat every 30 seconds (server-side)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isActiveRef = useRef(true)

  // Extract stable primitive values from profile to prevent unnecessary effect re-runs
  // This prevents the effect from re-running when React Query returns a new object reference
  // with the same data
  const profileEmail = userQueryData?.user?.profile?.email
  const profileFullName = userQueryData?.user?.profile?.fullName
  const profileFirstName = userQueryData?.user?.profile?.firstName
  const profileLastName = userQueryData?.user?.profile?.lastName
  const hasProfile = !!userQueryData?.user?.profile

  useEffect(() => {
    // Only send heartbeats if we have required data
    if (!hasMemberId || !workspaceId || !hasProfile) {
      return
    }

    // Store profile values in local variables for use in sendHeartbeat
    // These are captured in the closure and won't change during the interval
    const email = profileEmail
    const name =
      profileFullName ||
      `${profileFirstName || ''} ${profileLastName || ''}`.trim()

    const sendHeartbeat = async () => {
      if (!isActiveRef.current) return

      // Call server action (member_id comes from auth context automatically)
      await recordDashboardHeartbeat({
        workspaceId,
        email: email || undefined,
        name: name || undefined,
      })
    }

    // Send initial heartbeat
    sendHeartbeat()

    // Set up interval (30 seconds)
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000)

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        isActiveRef.current = true
        // Send heartbeat immediately when page becomes visible
        sendHeartbeat()
      } else {
        isActiveRef.current = false
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup
    return () => {
      isActiveRef.current = false
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
    // Use primitive values instead of object reference to prevent unnecessary re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMemberId,
    workspaceId,
    profileEmail,
    profileFullName,
    profileFirstName,
    profileLastName,
    hasProfile,
  ])

  // Record dashboard load end when dashboard is fully loaded and rendered
  useEffect(() => {
    const currentSessionId = loadSessionIdRef?.current ?? loadSessionId
    if (
      status === 'success' &&
      dashboardData &&
      workspaceId &&
      hasMemberId &&
      currentSessionId &&
      !hasRecordedLoadEndRef.current
    ) {
      console.warn('[Dashboard Load] ⚠️ Recording load end event:', {
        sessionId: currentSessionId,
        workspaceId,
        memberId: memberIdToUse,
      })

      // Record load end event (server-side GraphQL call, server calculates duration)
      // Include workspaceId and memberId now that we have them
      import('./performance-metrics').then(({ recordDashboardLoadEnd }) => {
        recordDashboardLoadEnd({
          sessionId: currentSessionId,
          workspaceId, // Now available from dashboard query
          memberId: memberIdToUse, // Now available from context
        })
          .then((result: any) => {
            const data = result?.recordDashboardLoadEnd
            if (data?.success) {
              console.warn(
                `[Dashboard Load] ⚠️ ✅ Recorded end event, duration=${data.duration?.toFixed(
                  2
                )}s`
              )
            } else {
              console.warn(
                '[Dashboard Load] ⚠️ Failed to record end event (non-critical):',
                data?.message
              )
            }
          })
          .catch((error) => {
            console.error(
              '[Dashboard Load] ❌ Error recording end event (non-critical):',
              error
            )
          })
      })

      // Mark as recorded to prevent duplicate calls
      hasRecordedLoadEndRef.current = true
    } else {
      console.debug('[Dashboard Load] End event conditions not met:', {
        status,
        hasDashboardData: !!dashboardData,
        workspaceId,
        hasMemberId,
        currentSessionId,
        hasRecorded: hasRecordedLoadEndRef.current,
      })
    }
  }, [
    status,
    dashboardData,
    workspaceId,
    hasMemberId,
    memberIdToUse,
    loadSessionId,
    loadSessionIdRef,
  ])

  // Memoize the user profile to prevent context value changes when object reference changes
  // but data is the same. This prevents unnecessary re-renders of all context consumers.
  const memoizedUserProfile = useMemo(() => {
    const profile = userQueryData?.user?.profile
    if (!profile) return undefined

    // Return a new object with the same values to ensure stable reference
    // Only recreate if the actual values change
    return {
      firstName: profile.firstName,
      lastName: profile.lastName,
      avatar: profile.avatar,
      email: profile.email,
      fullName: profile.fullName,
    }
  }, [
    userQueryData?.user?.profile?.firstName,
    userQueryData?.user?.profile?.lastName,
    userQueryData?.user?.profile?.avatar,
    userQueryData?.user?.profile?.email,
    userQueryData?.user?.profile?.fullName,
  ])

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => {
    const SYSTEM_USER_ID = '409b6857-a544-4aa2-a3f1-9747be7958d5'
    const ownerMemberId = dashboardData?.owner?.memberId || ''

    // Log if dashboard owner is system user (this is expected for system-owned dashboards)
    // but we should NEVER use owner.memberId for queries - always use memberId from useAppliedUserId
    if (ownerMemberId === SYSTEM_USER_ID && shouldDebug()) {
      console.warn(
        `[WARNING] Dashboard owner is system user ID: ${ownerMemberId}. ` +
          `This is expected for system-owned dashboards, but queries should use memberId from useAppliedUserId, not owner.memberId.`
      )
    }

    return {
      owner: {
        memberId: ownerMemberId,
      },
      workspace: {
        id: dashboardData?.workspace?.id || '',
        parentId: dashboardData?.workspace?.parentId || '',
        commonName: dashboardData?.workspace?.commonName || '',
        billingCode: dashboardData?.workspace?.billingCode || '',
        showBillingCode: dashboardData?.workspace?.showBillingCode ?? false,
        orgName: dashboardData?.workspace?.orgName || '',
        creatorId: dashboardData?.workspace?.creatorId || '',
      },
      member,
      memberId: memberIdToUse,
      workspaceMembers: {
        workspaceMembersQueryStatus,
        refetchWorkspaceMembers,
        members: workspaceMembers,
      },
      userProfile: memoizedUserProfile,
      status,
      refetch,
      findMemberById,
      findMemberEmail,
      clientSetup: clientSetupData,
    }
  }, [
    dashboardData?.owner?.memberId,
    dashboardData?.workspace?.id,
    dashboardData?.workspace?.parentId,
    dashboardData?.workspace?.commonName,
    dashboardData?.workspace?.billingCode,
    dashboardData?.workspace?.showBillingCode,
    dashboardData?.workspace?.orgName,
    dashboardData?.workspace?.creatorId,
    member,
    memberIdToUse,
    workspaceMembersQueryStatus,
    refetchWorkspaceMembers,
    workspaceMembers,
    memoizedUserProfile,
    status,
    refetch,
    findMemberById,
    findMemberEmail,
    clientSetupData,
  ])

  return (
    <DashboardWorkspaceContext.Provider value={contextValue}>
      {children}
    </DashboardWorkspaceContext.Provider>
  )
}

type IDashboardEvaluationsContext = {
  evaluations: DashboardEvaluationsQuery['dashboard']['evaluations']
  owner: {
    memberId: string
  }
  workspace: {
    id: string
  }
  refetch?: () => void
  status?: QueryStatus
  isLoading?: boolean
}

const defaultDashboardEvaluationsContextValue = {
  evaluations: [],
  owner: {
    memberId: '',
  },
  workspace: {
    id: '',
  },
  status: undefined,
  isLoading: false,
}

export const DashboardEvaluationsContext =
  createContext<IDashboardEvaluationsContext>(
    defaultDashboardEvaluationsContextValue
  )

export const useDashboardEvaluationsContext = () =>
  useContext(DashboardEvaluationsContext)

export const DashboardEvaluationsContextProvider = ({ children }) => {
  // Get memberId from workspace context to determine if we should enable the query
  // We need to check if memberId is available before making the query
  const { memberId } = useDashboardWorkspaceContext()
  const hasMemberId = !!memberId
  const { status, data, refetch, isLoading } =
    useDashboardEvaluationsQuery(hasMemberId)

  // Move debug logging to useEffect to prevent logging on every render
  useEffect(() => {
    if (shouldDebug()) {
      console.log(
        `[DEBUG] DashboardEvaluationsContext: status=${status}, isLoading=${isLoading}, hasData=${!!data}, evaluationsCount=${
          data?.dashboard?.evaluations?.length ?? 0
        }, timestamp=${new Date().toISOString()}`
      )
    }
  }, [status, isLoading, data])

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      evaluations: data?.dashboard?.evaluations ?? [],
      owner: {
        memberId: data?.dashboard?.owner?.memberId || '',
      },
      workspace: {
        id: data?.dashboard?.workspace?.id || '',
      },
      status,
      isLoading,
      refetch,
    }),
    [
      data?.dashboard?.evaluations,
      data?.dashboard?.owner?.memberId,
      data?.dashboard?.workspace?.id,
      status,
      isLoading,
      refetch,
    ]
  )

  return (
    <DashboardEvaluationsContext.Provider value={contextValue}>
      {children}
    </DashboardEvaluationsContext.Provider>
  )
}
